import type { ActivityPriority, ActivityStatus, ActivityType } from "@torre/work-hours";
import type { ActivityRow } from "../services/activitiesService";

// ---------------------------------------------------------------------------
// Helpers de presentación de Actividades (épica #1299): colores por estado y
// prioridad, formato de horas, dinero e iniciales. Sin dependencias.
// ---------------------------------------------------------------------------

/** Columnas del Kanban, en orden. El backlog no es columna: vive en su sección. */
export const KANBAN_STATUSES: ActivityStatus[] = ["todo", "in_progress", "review", "blocked", "done"];

export const STATUS_TONE: Record<ActivityStatus, string> = {
  backlog: "bg-gray-400/15 text-gray-500",
  todo: "bg-sky-500/15 text-sky-600 dark:text-sky-400",
  in_progress: "bg-violet-500/15 text-violet-600 dark:text-violet-400",
  review: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  blocked: "bg-red-500/15 text-red-600 dark:text-red-400",
  done: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
};

export const PRIORITY_TONE: Record<ActivityPriority, string> = {
  low: "text-gray-400",
  medium: "text-sky-500",
  high: "text-amber-500",
  urgent: "text-red-500",
};

export const PRIORITY_ICON: Record<ActivityPriority, string> = {
  low: "ri-arrow-down-line",
  medium: "ri-subtract-line",
  high: "ri-arrow-up-line",
  urgent: "ri-alarm-warning-line",
};

/** Borde izquierdo de la tarjeta por prioridad (#1595): se lee de un vistazo en el tablero. */
export const PRIORITY_ACCENT: Record<ActivityPriority, string> = {
  low: "border-l-gray-300 dark:border-l-gray-600",
  medium: "border-l-sky-400",
  high: "border-l-amber-500",
  urgent: "border-l-red-500",
};

/** Color de la franja superior de cada columna del tablero (#1595). */
export const STATUS_BAR: Record<ActivityStatus, string> = {
  backlog: "bg-gray-400",
  todo: "bg-sky-500",
  in_progress: "bg-violet-500",
  review: "bg-amber-500",
  blocked: "bg-red-500",
  done: "bg-emerald-500",
};

/** Etiquetas del ticket: las propias (`labels`) o las del issue de GitHub. */
export function activityLabels(a: Pick<ActivityRow, "github"> & { labels?: unknown }): string[] {
  const own = Array.isArray(a.labels) ? a.labels : [];
  const list = own.length ? own : a.github?.labels ?? [];
  return [...new Set(list.map((l) => String(l).trim()).filter(Boolean))];
}

const PRIORITY_RANK: Record<ActivityPriority, number> = { urgent: 0, high: 1, medium: 2, low: 3 };

/** Orden del backlog: prioridad, luego fecha de entrega (sin fecha al final), luego clave. */
export function compareBacklog(a: ActivityRow, b: ActivityRow): number {
  const p = (PRIORITY_RANK[a.priority] ?? 2) - (PRIORITY_RANK[b.priority] ?? 2);
  if (p) return p;
  if (a.dueDate !== b.dueDate) {
    if (!a.dueDate) return 1;
    if (!b.dueDate) return -1;
    return a.dueDate.localeCompare(b.dueDate);
  }
  return String(a.key).localeCompare(String(b.key));
}

/** ¿El ticket viene de GitHub (issue, PR o milestone)? */
export const isGithubActivity = (a: Pick<ActivityRow, "source">): boolean => String(a.source ?? "").startsWith("github_");

/**
 * #1404 / #1419: ¿el issue de GitHub tiene un ticket asociado? Lo tiene si
 * ya se le registraron horas, si está en un sprint, si está ligado (como
 * padre o hijo) a un ticket propio del equipo, o si un ticket de la Mesa de
 * Servicio lo trae ligado (`serviceDesk`, lo marca la sincronización). Lo
 * demás es ruido del repo.
 */
export function githubHasLinkedWork(a: ActivityRow, all: readonly ActivityRow[]): boolean {
  if ((Number(a.loggedMinutes) || 0) > 0 || a.sprintId || a.serviceDesk?.ticketId) return true;
  if (a.parentId && all.some((b) => b.id === a.parentId && !isGithubActivity(b))) return true;
  return all.some((b) => b.parentId === a.id && !isGithubActivity(b));
}

/**
 * #1404: filtro del interruptor «Issues de GitHub». Apagado: solo los tickets
 * propios. Encendido: además, los issues de GitHub con trabajo asociado.
 */
export function filterGithub(rows: readonly ActivityRow[], all: readonly ActivityRow[], showGithub: boolean): ActivityRow[] {
  return rows.filter((a) => !isGithubActivity(a) || (showGithub && githubHasLinkedWork(a, all)));
}

/** `90` → `1.5 h`; `30` → `30 min`. */
export function formatMinutes(minutes: number | null | undefined): string {
  const m = Math.round(Number(minutes) || 0);
  if (m <= 0) return "0 h";
  if (m < 60) return `${m} min`;
  const h = m / 60;
  return `${Number.isInteger(h) ? h : h.toFixed(h < 10 ? 1 : 0)} h`;
}

/** Horas decimales → minutos enteros (entrada del formulario). */
export const hoursToMinutes = (hours: string | number): number | null => {
  const n = Number(hours);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 60);
};

export function formatMoney(value: number | null | undefined, currency = "MXN"): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  try {
    return new Intl.NumberFormat("es-MX", { style: "currency", currency, maximumFractionDigits: 0 }).format(value);
  } catch {
    return `${value.toFixed(0)} ${currency}`;
  }
}

export function initials(name: string | null | undefined): string {
  const parts = String(name ?? "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts[1]?.[0] ?? "")).toUpperCase();
}

/** Fecha local `YYYY-MM-DD` de hoy (zona del navegador). */
export function todayLocal(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export const isOverdue = (a: Pick<ActivityRow, "dueDate" | "status">, today = todayLocal()): boolean =>
  Boolean(a.dueDate) && a.status !== "done" && (a.dueDate as string) < today;

/** `2026-10-05` → `5 oct`. */
export function shortDate(date: string | null | undefined): string {
  if (!date) return "—";
  const [y, m, d] = date.split("-").map(Number);
  if (!y || !m || !d) return date;
  return new Date(y, m - 1, d).toLocaleDateString("es-MX", { day: "numeric", month: "short" });
}

/** Tipo del catálogo por clave, con un comodín para claves que ya no existen. */
export function typeOf(catalog: readonly ActivityType[], key: string): Pick<ActivityType, "key" | "name" | "icon"> {
  return catalog.find((t) => t.key === key) ?? { key, name: key, icon: "ri-clipboard-line" };
}

/** Enlace a GitHub del ticket (issue/PR/milestone), si lo tiene. */
export function githubUrl(a: Pick<ActivityRow, "github">): string | null {
  if (!a.github) return null;
  if (a.github.url) return a.github.url;
  const base = `https://github.com/${a.github.owner}/${a.github.repo}`;
  if (a.github.isMilestone) return `${base}/milestone/${a.github.number}`;
  return `${base}/${a.github.isPullRequest ? "pull" : "issues"}/${a.github.number}`;
}

/** Semanas (lunes) que cubren un rango de fechas, para la línea de tiempo del roadmap. */
export function weeksBetween(from: string, to: string): string[] {
  const start = new Date(`${from}T00:00:00`);
  const day = start.getDay();
  start.setDate(start.getDate() - ((day + 6) % 7)); // lunes
  const end = new Date(`${to}T00:00:00`);
  const out: string[] = [];
  const p = (n: number) => String(n).padStart(2, "0");
  for (let d = new Date(start); d <= end && out.length < 60; d.setDate(d.getDate() + 7)) {
    out.push(`${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`);
  }
  return out;
}

export const dayDiff = (from: string, to: string): number => Math.round((Date.parse(`${to}T00:00:00`) - Date.parse(`${from}T00:00:00`)) / 86400000);
